import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Activity, RefreshCw, Loader2, Eye, EyeOff,
  Receipt, ShoppingBag, Home, Gamepad2, Dice5, Wine, Banknote, Wallet,
  Tv, UtensilsCrossed, ShoppingCart, Coffee, Car, Plane,
  Sparkles, Dumbbell, HeartPulse, GraduationCap, CreditCard, Gift, PawPrint,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const CATEGORY_META = {
  BILLS:            { label: "Bills",             Icon: Receipt,        color: "from-sky-500 to-blue-600",       chip: "bg-sky-100 text-sky-700" },
  HOUSING:          { label: "Housing",           Icon: Home,           color: "from-emerald-500 to-emerald-600", chip: "bg-emerald-100 text-emerald-700" },
  STREAMING:        { label: "Streaming",         Icon: Tv,             color: "from-fuchsia-500 to-pink-600",   chip: "bg-fuchsia-100 text-fuchsia-700" },
  FOOD_DELIVERY:    { label: "Food delivery",     Icon: UtensilsCrossed, color: "from-orange-500 to-red-500",    chip: "bg-orange-100 text-orange-700" },
  GROCERIES:        { label: "Groceries",         Icon: ShoppingCart,   color: "from-lime-500 to-green-600",     chip: "bg-lime-100 text-lime-800" },
  DINING:           { label: "Dining out",        Icon: Coffee,         color: "from-amber-600 to-orange-600",   chip: "bg-amber-100 text-amber-800" },
  TRANSPORT:        { label: "Transport",         Icon: Car,            color: "from-cyan-500 to-teal-600",      chip: "bg-cyan-100 text-cyan-700" },
  TRAVEL:           { label: "Travel",            Icon: Plane,          color: "from-indigo-500 to-blue-700",    chip: "bg-indigo-100 text-indigo-700" },
  SHOPPING:         { label: "Shopping",          Icon: ShoppingBag,    color: "from-amber-500 to-orange-500",   chip: "bg-amber-100 text-amber-800" },
  PERSONAL_CARE:    { label: "Personal care",     Icon: Sparkles,       color: "from-pink-500 to-rose-500",      chip: "bg-pink-100 text-pink-700" },
  FITNESS:          { label: "Fitness",           Icon: Dumbbell,       color: "from-emerald-500 to-teal-600",   chip: "bg-emerald-100 text-emerald-700" },
  HEALTH:           { label: "Health & medical",  Icon: HeartPulse,     color: "from-red-500 to-rose-600",       chip: "bg-red-100 text-red-700" },
  EDUCATION:        { label: "Education",         Icon: GraduationCap,  color: "from-blue-500 to-indigo-600",    chip: "bg-blue-100 text-blue-700" },
  ONLINE_GAMES:     { label: "Online games",      Icon: Gamepad2,       color: "from-violet-500 to-fuchsia-600", chip: "bg-violet-100 text-violet-700" },
  GAMBLING:         { label: "Gambling",          Icon: Dice5,          color: "from-rose-500 to-red-600",       chip: "bg-rose-100 text-rose-700" },
  ALCOHOL:          { label: "Alcohol",           Icon: Wine,           color: "from-red-500 to-red-700",        chip: "bg-red-100 text-red-700" },
  CASH_WITHDRAWALS: { label: "Cash withdrawals",  Icon: Banknote,       color: "from-slate-700 to-slate-900",    chip: "bg-slate-200 text-slate-800" },
  FEES:             { label: "Bank fees",         Icon: CreditCard,     color: "from-red-600 to-slate-800",      chip: "bg-red-100 text-red-800" },
  GIFTS:            { label: "Gifts & donations", Icon: Gift,           color: "from-pink-500 to-purple-600",    chip: "bg-pink-100 text-pink-700" },
  PETS:             { label: "Pets",              Icon: PawPrint,       color: "from-amber-500 to-yellow-600",   chip: "bg-amber-100 text-amber-800" },
  OTHER:            { label: "Other",             Icon: Wallet,         color: "from-slate-400 to-slate-600",    chip: "bg-slate-100 text-slate-700" },
};

const RANGES = [
  { value: 7,   label: "Last 7 days" },
  { value: 30,  label: "Last 30 days" },
  { value: 90,  label: "Last 90 days" },
  { value: 365, label: "Last 12 months" },
];

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [syncing, setSyncing] = useState(false);
  const [hidden, setHidden] = useState(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem("mmh_hidden_categories");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  const load = async (range) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/spending?days=${range}`);
      setData(data);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/analytics/sync");
      toast.success(r.added > 0 ? `Synced ${r.added} new transactions` : "All caught up");
      await load(days);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 202 || detail?.code === "PRODUCT_NOT_READY") {
        toast.message("Plaid is preparing your transactions…", {
          description: "Try again in ~30 seconds.",
        });
      } else if (e.response?.status === 400) {
        toast.error("Connect a bank first.");
      } else {
        toast.error("Sync failed.");
      }
    } finally {
      setSyncing(false);
    }
  };

  const toggleCategory = (key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try { window.localStorage.setItem("mmh_hidden_categories", JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const visibleCategories = useMemo(
    () => (data?.categories || []).filter((c) => !hidden.has(c.key)),
    [data, hidden]
  );

  const visibleOutflow = useMemo(
    () => visibleCategories.reduce((sum, c) => sum + c.total, 0),
    [visibleCategories]
  );

  const maxTotal = useMemo(
    () => Math.max(1, ...visibleCategories.map((c) => c.total)),
    [visibleCategories]
  );

  return (
    <div className="hero-theme min-h-screen" data-testid="analytics-page">
      <div className="max-w-2xl mx-auto w-full pt-8 pb-16 px-5">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
          data-testid="analytics-back-button"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </button>

        {/* Header */}
        <div className="mt-4 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Spending</p>
            <h1
              className="text-3xl font-black text-slate-900 leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Analytics
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="h-9 w-[160px] rounded-xl border-2 border-slate-200 bg-white text-xs font-extrabold" data-testid="analytics-range-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => (
                  <SelectItem key={r.value} value={String(r.value)} data-testid={`analytics-range-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              onClick={handleSync}
              disabled={syncing}
              className="h-9 rounded-xl bg-slate-900 hover:bg-slate-800 text-amber-400 font-extrabold gap-1.5"
              data-testid="analytics-sync-button"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={2.8} />}
              Sync
            </Button>
          </div>
        </div>

        {/* Summary tile */}
        <div className="mt-5 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-44 h-44 bg-amber-400/20 rounded-full blur-3xl" aria-hidden="true" />
          <div className="relative">
            <div className="flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-amber-300" strokeWidth={2.6} />
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-300">Outflow · visible</span>
            </div>
            <p
              className="mt-1.5 text-4xl font-black tabular-nums leading-none"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="analytics-total-outflow"
            >
              ${visibleOutflow.toFixed(2)}
            </p>
            <p className="mt-1 text-[11px] font-bold text-slate-300">
              {data?.transaction_count || 0} txns · ${data?.total_outflow?.toFixed(2) || "0.00"} all categories · ${data?.total_inflow?.toFixed(2) || "0.00"} inflow
            </p>
          </div>
        </div>

        {/* Categories */}
        <div className="mt-6 flex flex-col gap-2.5" data-testid="analytics-categories">
          {loading && (
            <div className="rounded-2xl bg-white border-2 border-slate-100 p-5 text-center text-slate-400 font-semibold">
              Loading…
            </div>
          )}
          {!loading && (data?.categories || []).map((cat) => {
            const meta = CATEGORY_META[cat.key] || CATEGORY_META.OTHER;
            const Icon = meta.Icon;
            const isHidden = hidden.has(cat.key);
            const pct = isHidden ? 0 : Math.round((cat.total / maxTotal) * 100);
            return (
              <div
                key={cat.key}
                className={`rounded-2xl border-2 p-4 transition-all ${
                  isHidden ? "bg-slate-50 border-dashed border-slate-200 opacity-60" : "bg-white border-slate-100"
                }`}
                data-testid={`analytics-category-${cat.key}`}
                data-hidden={isHidden ? "true" : "false"}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${meta.color} text-white shadow-md`}>
                    <Icon className="w-5 h-5" strokeWidth={2.6} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p
                        className={`text-base font-black ${isHidden ? "text-slate-500" : "text-slate-900"}`}
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        {meta.label}
                      </p>
                      <span className={`text-base font-black tabular-nums whitespace-nowrap ${
                        isHidden ? "text-slate-400 line-through decoration-2 decoration-slate-300" : "text-slate-900"
                      }`}>
                        ${cat.total.toFixed(2)}
                      </span>
                    </div>
                    <p className="text-[11px] font-semibold text-slate-500 mt-0.5">
                      {cat.count} txn{cat.count === 1 ? "" : "s"}
                    </p>
                    {!isHidden && (
                      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${meta.color}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleCategory(cat.key)}
                    className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                      isHidden
                        ? "bg-white text-slate-500 hover:text-slate-900 border border-slate-200"
                        : "bg-slate-900 text-white hover:bg-slate-800"
                    }`}
                    aria-pressed={!isHidden}
                    data-testid={`analytics-toggle-${cat.key}`}
                  >
                    {isHidden ? <EyeOff className="w-4 h-4" strokeWidth={2.6} /> : <Eye className="w-4 h-4" strokeWidth={2.6} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {!loading && (!data?.categories || data.categories.length === 0) && (
          <div className="mt-6 rounded-2xl bg-white border-2 border-dashed border-slate-200 p-8 text-center" data-testid="analytics-empty">
            <Activity className="w-9 h-9 mx-auto text-slate-300" />
            <p className="mt-2 text-sm font-bold text-slate-700">No transactions yet.</p>
            <p className="text-xs text-slate-500 mt-0.5">Connect a bank from your dashboard, then tap Sync.</p>
          </div>
        )}
      </div>
    </div>
  );
}
